import {
  Button,
  Card,
  CardContent,
  Progress,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@nfp-tracker/components";
import { Sparkles, X } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { ROUTES } from "../constants/routes";
import { useOnboardingChecklist } from "../hooks/useOnboardingChecklist";
import { trackEvent } from "../integrations/analytics/client";

/**
 * Compact, dismissible getting-started progress card shown on the home screen to
 * nudge users toward finishing setup. Links to the full Getting Started page.
 */
export const GettingStartedCard = () => {
  const navigate = useNavigate();
  const {
    loading,
    steps,
    releaseStep,
    completedCount,
    totalCount,
    percentComplete,
    allComplete,
    homeCardDismissed,
    dismissHomeCard,
  } = useOnboardingChecklist();

  const show = !loading && !allComplete && !homeCardDismissed;

  // Deliberately no impression event: the card renders on every visit to the home screen,
  // so a mount-time event fired once per navigation and drowned out the interaction events
  // below. Card visibility is already derivable from the checklist state on the user.

  if (!show) return null;

  // Setup already finished and only a new release is left to review: say so, instead of
  // telling a long-time user to "finish setting up".
  const onlyReleasePending =
    Boolean(releaseStep && !releaseStep.completed) &&
    steps.every((s) => s.completed || s.dismissed);

  return (
    <Card className="border-primary/30 shadow-sm">
      <CardContent className="flex items-center gap-4 p-4">
        <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-gradient-to-br from-primary to-fuchsia-500 text-primary-foreground">
          <Sparkles className="h-5 w-5" />
        </div>
        <div className="min-w-0 flex-1">
          <p className="font-semibold leading-tight">
            {onlyReleasePending ? "See what's new" : "Finish setting up"}
          </p>
          <p className="text-sm text-muted-foreground">
            {onlyReleasePending
              ? "A few new features are ready to try"
              : `${completedCount} of ${totalCount} steps done`}
          </p>
          <Progress value={percentComplete} className="mt-2 h-2" />
        </div>
        <Button
          size="sm"
          className="shrink-0"
          onClick={() => {
            trackEvent("getting_started_card_cta_clicked", {
              completedCount,
              totalCount,
            });
            navigate(ROUTES.gettingStarted);
          }}
        >
          {onlyReleasePending ? "Take a look" : "Continue"}
        </Button>
        <Tooltip>
          <TooltipTrigger asChild>
            <button
              type="button"
              aria-label="Dismiss setup checklist"
              className="shrink-0 rounded-md p-1 text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
              onClick={() => {
                trackEvent("getting_started_card_dismissed", {
                  completedCount,
                  totalCount,
                });
                void dismissHomeCard();
              }}
            >
              <X className="h-4 w-4" />
            </button>
          </TooltipTrigger>
          <TooltipContent side="top">Dismiss</TooltipContent>
        </Tooltip>
      </CardContent>
    </Card>
  );
};
